import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LanguageNameSpaces, OverrideFieldType } from "@shared/types";
import {
  ChannelValue,
  MultiSymbolMarginData,
  SymbolMarginData,
} from "@shared/features/bots/BotForm/common/types";
import { isSymbol } from "@shared/types/utils";
import { usePairsSelectors } from "@shared/features/bots/BotForm/common/hooks";
import {
  channelValue,
  MARGIN_GLOBAL_SETTINGS_KEY,
} from "@shared/features/bots/BotForm/common/constants";
import useFormHelperData from "@shared/features/bots/BotForm/common/hooks/useFormHelperData";
import { MultiSymbolMarginContext } from "@shared/features/bots/BotForm/MultiSymbolMarginSelector/MultiSymbolMargin.context";
import { getDefaultGlobalSettings } from "@shared/features/bots/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useMultiSymbolMarginSelectorProps {
  navigateBack?: () => void;
}

const useMultiSymbolMarginSelector = ({
  navigateBack,
}: useMultiSymbolMarginSelectorProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t: tBots } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    allSymbols,
    searchFilteredSymbols,
    selectedSymbols,
    setIsSearchOpen,
    resetData,
    setSelectedSymbolsAndPendingValues,
    multiSymbolMarginData,
    updatedMultiSymbolMarginData,
    setSearchValue,
    lastModifiedMultiSymbolMarginData,
    updateLastModifiedData,
    selectorPreview,
    hasPendingChanges,
    hasLeverageValueModifiedError,
    botFormDataRef,
    isViewOnly,
    isSymbolsOnly,
    alwaysDisplay,
    showOnlyModifiedSymbols,
    setShowOnlyModifiedSymbols,
    pendingGlobalMarginType,
    pendingGlobalMultiplier,
    pendingGlobalLeverageType,
    initializedExchange,
    setGlobalSettingsExpanded,
    isFullScreen,
  } = useContext(MultiSymbolMarginContext)!;

  const {
    multiSymbolMarginData: formMultiSymbolMarginData,
    setMultiSymbolMarginData,
    isAdvancedView,
    toggleOverrideField,
    isBacktesting,
    isEdit: formIsEdit,
  } = botFormDataRef.current || {};

  const { getHelperData } = useFormHelperData();
  const helperData = getHelperData("multiSymbolMarginData");
  const originalMarginModifiedHelperData = getHelperData(
    "originalLeverageModifiedWarning"
  );
  const [isOpen, setIsOpen] = useState(false);

  const [confirmCloseModalOpen, setConfirmCloseModalOpen] = useState(false);

  const isFormDataChannelValue = useMemo(() => {
    return (
      isSymbol(formMultiSymbolMarginData) ||
      !formMultiSymbolMarginData ||
      !Object.values(formMultiSymbolMarginData).some((symbolMarginData) => {
        return Object.values(symbolMarginData).some(
          (value) => !isSymbol(value)
        );
      })
    );
  }, [formMultiSymbolMarginData]);

  const pendingGlobalSettings = {
    marginType: pendingGlobalMarginType ?? channelValue,
    multiplier: pendingGlobalMultiplier ?? channelValue,
    leverageType: pendingGlobalLeverageType ?? channelValue,
  };

  const setSelectedSymbols = useCallback(
    (symbols: string[]) => setSelectedSymbolsAndPendingValues(new Set(symbols)),
    [setSelectedSymbolsAndPendingValues]
  );

  const { pairOptions } = usePairsSelectors({
    allSymbols: searchFilteredSymbols,
    selectedSymbols: selectedSymbols,
    setSelectedSymbols,
  });

  const resetLeverageOverride = () => {
    toggleOverrideField?.(OverrideFieldType.LEVERAGE, true);
  };

  useEffect(() => {
    if (
      !isFormDataChannelValue &&
      !formIsEdit &&
      !isBacktesting &&
      initializedExchange &&
      !isFullScreen
    ) {
      resetLeverageOverride();
    }
  }, [isFormDataChannelValue]);

  const updateMarginData = () => {
    const updatedMarginData = { ...multiSymbolMarginData };
    const updatedLastModifiedMarginData: MultiSymbolMarginData = {};
    selectedSymbols.forEach((symbol) => {
      const symbolMarginData = { ...updatedMarginData[symbol] };

      const { marginType, multiplier, leverageType } =
        updatedMultiSymbolMarginData[symbol] || {};
      const updatedLastModifiedData: SymbolMarginData = {
        ...lastModifiedMultiSymbolMarginData[symbol],
      };
      if (multiplier) {
        symbolMarginData.multiplier = multiplier;
        updatedLastModifiedData.multiplier = multiplier;
      }
      if (marginType) {
        symbolMarginData.marginType = marginType;
        updatedLastModifiedData.marginType = marginType;
      }
      if (leverageType) {
        symbolMarginData.leverageType = leverageType;
        updatedLastModifiedData.leverageType = leverageType;
      }
      updatedMarginData[symbol] = symbolMarginData;
      updatedLastModifiedMarginData[symbol] = updatedLastModifiedData;
    });
    let parsedUpdatedMarginData: ChannelValue | MultiSymbolMarginData =
      updatedMarginData;
    parsedUpdatedMarginData[MARGIN_GLOBAL_SETTINGS_KEY] = pendingGlobalSettings;
    setMultiSymbolMarginData?.(parsedUpdatedMarginData);
    updateLastModifiedData(updatedLastModifiedMarginData);
  };

  const onApply = () => {
    updateMarginData();
    resetData(false, true);
  };

  const onOk = () => {
    onApply();
    setIsSearchOpen(false);
    setSearchValue("");
    setIsOpen(false);
    navigateBack?.();
  };

  const onCancel = () => {
    resetData(true, true);
    setGlobalSettingsExpanded(true);
    setIsSearchOpen(false);
    setSearchValue("");
    setIsOpen(false);
    navigateBack?.();
  };

  const footerData = {
    onOk,
    onApply,
    onCancel: () => {
      if (hasPendingChanges) {
        setConfirmCloseModalOpen(true);
      } else onCancel();
    },
    applyButtonDisabled: !hasPendingChanges,
  };

  const confirmModalData = {
    isOpen: confirmCloseModalOpen,
    onCancel: () => setConfirmCloseModalOpen(false),
    title: "Unsaved changes",
    texts: [
      "You have unsaved changes. Proceeding with this action will discard them unless you click the 'OK' or 'Apply' buttons below.",
      "Are you sure you wish to proceed and discard your changes?",
    ],
    onOk: () => {
      setConfirmCloseModalOpen(false);
      onCancel();
    },
    openModal: () => setConfirmCloseModalOpen(true),
    okText: t("labels.yes"),
    cancelText: t("labels.no"),
  };

  return {
    isOpen,
    setIsOpen,
    pairOptions,
    footerData,
    selectorPreview,
    title: isSymbolsOnly
      ? `${tBots("labels.symbols")} (${allSymbols.length})`
      : tBots("labels.margin"),
    confirmModalData,
    hasPendingChanges,
    shouldDisplay:
      isAdvancedView || isAdvancedView === undefined || alwaysDisplay,
    helperData,
    originalMarginModifiedHelperData,
    hasLeverageValueModifiedError:
      hasLeverageValueModifiedError && !isViewOnly && !isSymbolsOnly,
    updateLastModifiedData,
    isViewOnly,
    showOnlyModifiedSymbols,
    setShowOnlyModifiedSymbols,
    defaultChannelValue: getDefaultGlobalSettings(),
    resetLeverageOverride,
  };
};

export default useMultiSymbolMarginSelector;
